import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { api, CallListAgent, ProjectActionList } from '../../../services/api';
import { paHeadCellSx, ProjectOption } from './shared';

interface ListDraft {
  title: string;
  sqlStatement: string;
  whatsappMessage: string;
}

const NEW_LIST_ID = -1;

// Qualified three-part names: the agent/listing tables live in HuttonsApp, while
// the app connects to HuttonsPortal, so an unqualified name fails to resolve.
const SQL_PLACEHOLDER = `select u.BusinessName, u.Cea, u.Mobile,
       CONCAT(COUNT(DISTINCT pl.Id), ' listings') AS remarks
FROM HuttonsApp.dbo.PGListings pl
JOIN HuttonsApp.dbo.PGListingAgents pla ON pla.ListingId = pl.Id
JOIN HuttonsApp.dbo.[User] u ON u.Cea = pla.AlternativeCeaRegistrationNo
...
group by u.BusinessName, u.Cea, u.Mobile`;

const DEFAULT_MESSAGE =
  'Hi {name}, this is {caller} from {project}. Would you have a moment for a quick chat?';

function emptyDraft(): ListDraft {
  return { title: '', sqlStatement: '', whatsappMessage: DEFAULT_MESSAGE };
}

function draftFromList(list: ProjectActionList): ListDraft {
  return {
    title: list.title,
    sqlStatement: list.sqlStatement,
    whatsappMessage: list.whatsappMessage || DEFAULT_MESSAGE,
  };
}

/**
 * Management's side: write a cohort query against a project, see who it catches,
 * and put those agents on that project. Nothing is assigned to a person here —
 * the project's lead shares the list out across their core team.
 */
const ListsTab: React.FC<{ projects: ProjectOption[]; onChanged?: () => void }> = ({
  projects,
  onChanged,
}) => {
  const [lists, setLists] = useState<ProjectActionList[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<ListDraft>(emptyDraft());
  const [project, setProject] = useState<ProjectOption | null>(null);
  const [agents, setAgents] = useState<CallListAgent[] | null>(null);
  const [selectedCeas, setSelectedCeas] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const isNew = selectedId === NEW_LIST_ID;
  const selectedList = useMemo(
    () => lists.find((l) => l.id === selectedId) || null,
    [lists, selectedId]
  );

  const loadLists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getProjectActionLists();
      setLists(response.lists);
      setSelectedId((current) => {
        if (current === NEW_LIST_ID) return current;
        if (current && response.lists.some((l) => l.id === current)) return current;
        return response.lists[0]?.id ?? NEW_LIST_ID;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  // Switching lists resets the preview — the agents on screen always belong to
  // the list being edited.
  useEffect(() => {
    setAgents(null);
    setSelectedCeas(new Set());
    setNotice(null);
    if (isNew || selectedId === null) {
      setDraft(emptyDraft());
      setProject(null);
      return;
    }
    const list = lists.find((l) => l.id === selectedId);
    if (!list) return;
    setDraft(draftFromList(list));
    setProject(
      projects.find((p) => p.projectId === list.projectId) || {
        projectId: list.projectId,
        projectName: list.projectName,
      }
    );
  }, [selectedId, lists, isNew, projects]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (isNew) {
        if (!project) {
          setError('Pick the project this list belongs to');
          return;
        }
        const response = await api.createProjectActionList({
          projectId: project.projectId,
          title: draft.title.trim(),
          sqlStatement: draft.sqlStatement,
          whatsappMessage: draft.whatsappMessage,
        });
        await loadLists();
        setSelectedId(response.list.id);
        onChanged?.();
        setNotice('List created.');
      } else if (selectedId) {
        await api.updateProjectActionList(selectedId, {
          title: draft.title.trim(),
          sqlStatement: draft.sqlStatement,
          whatsappMessage: draft.whatsappMessage,
        });
        await loadLists();
        setNotice('List saved.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the list');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedList) return;
    const confirmed = window.confirm(
      `Delete the list "${selectedList.title}"? Its ${selectedList.taskCount} agent${
        selectedList.taskCount === 1 ? '' : 's'
      } will be withdrawn from the project.`
    );
    if (!confirmed) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteProjectActionList(selectedList.id);
      setSelectedId(null);
      await loadLists();
      onChanged?.();
      setNotice('List deleted.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete the list');
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    setNotice(null);
    try {
      // Runs the SQL in the editor, not the saved copy, so the query can be
      // iterated on before it is committed to the list.
      const response = await api.previewProjectActionList({ sqlStatement: draft.sqlStatement });
      setAgents(response.agents);
      setSelectedCeas(new Set(response.agents.map((a) => a.cea)));
      if (response.agents.length === 0) setNotice('The query returned no agents.');
    } catch (err) {
      setAgents(null);
      setError(err instanceof Error ? err.message : 'Failed to run the query');
    } finally {
      setRunning(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedList || !agents) return;
    const chosen = agents.filter((a) => selectedCeas.has(a.cea));
    if (chosen.length === 0) return;
    setAdding(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.addProjectActionAgents(selectedList.id, chosen);
      await loadLists();
      onChanged?.();
      setNotice(
        `Added ${result.created} agent${result.created === 1 ? '' : 's'} to ${
          selectedList.projectName || selectedList.projectId
        }` +
          (result.refreshed ? `, refreshed ${result.refreshed} already there.` : '.') +
          ' The project’s lead assigns them to the core team.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add agents');
    } finally {
      setAdding(false);
    }
  };

  const toggleAgent = (cea: string) => {
    setSelectedCeas((prev) => {
      const next = new Set(prev);
      if (next.has(cea)) next.delete(cea);
      else next.add(cea);
      return next;
    });
  };

  const toggleAll = () => {
    if (!agents) return;
    setSelectedCeas((prev) =>
      prev.size === agents.length ? new Set() : new Set(agents.map((a) => a.cea))
    );
  };

  const canSave =
    draft.title.trim().length > 0 &&
    draft.sqlStatement.trim().length > 0 &&
    (!isNew || Boolean(project)) &&
    !saving;
  const selectedCount = agents ? agents.filter((a) => selectedCeas.has(a.cea)).length : 0;

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <Box
          sx={{
            width: { xs: '100%', md: 260 },
            flexShrink: 0,
            border: '1px solid rgb(var(--color-text-rgb) / 0.1)',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Button
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => setSelectedId(NEW_LIST_ID)}
            sx={{ justifyContent: 'flex-start', px: 2, py: 1.25, textTransform: 'none' }}
          >
            New list
          </Button>
          <Divider />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 'min(50vh, 460px)', overflow: 'auto' }}>
              {lists.map((list) => (
                <ListItemButton
                  key={list.id}
                  selected={list.id === selectedId}
                  onClick={() => setSelectedId(list.id)}
                >
                  <ListItemText
                    primary={list.title}
                    secondary={`${list.projectName || list.projectId} · ${list.taskCount} agents`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              ))}
              {lists.length === 0 && (
                <Box sx={{ px: 2, py: 2 }}>
                  <Typography variant="caption" sx={{ color: 'rgb(var(--color-text-rgb) / 0.55)' }}>
                    No lists yet.
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={2}>
            <Autocomplete
              size="small"
              options={projects}
              value={project}
              onChange={(_e, value) => setProject(value)}
              getOptionLabel={(option) => option.projectName || option.projectId}
              isOptionEqualToValue={(a, b) => a.projectId === b.projectId}
              // A list's project is fixed once created: moving it would orphan
              // the agents already sitting on the original project's board.
              disabled={!isNew}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  helperText={isNew ? 'Which project these agents are called for' : 'Fixed once created'}
                />
              )}
            />
            <TextField
              label="List title"
              size="small"
              fullWidth
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              placeholder="e.g. Landed resale listers — D09/10/11/19/20/23"
              disabled={saving}
            />
            <TextField
              label="SQL statement"
              multiline
              minRows={8}
              maxRows={18}
              fullWidth
              value={draft.sqlStatement}
              onChange={(e) => setDraft((d) => ({ ...d, sqlStatement: e.target.value }))}
              placeholder={SQL_PLACEHOLDER}
              disabled={saving}
              helperText="Read-only SELECT. Must return a CEA column; BusinessName / Mobile / remarks are picked up when present. Qualify tables in other databases, e.g. HuttonsApp.dbo.PGListings."
              InputProps={{
                sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 },
              }}
            />
            <TextField
              label="WhatsApp message"
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              value={draft.whatsappMessage}
              onChange={(e) => setDraft((d) => ({ ...d, whatsappMessage: e.target.value }))}
              disabled={saving}
              helperText="Tokens: {name} {cea} {project} {remarks} {caller}"
            />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                {saving ? 'Saving…' : isNew ? 'Create list' : 'Save list'}
              </Button>
              <Button
                variant="outlined"
                startIcon={running ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                onClick={handleRun}
                disabled={running || !draft.sqlStatement.trim()}
              >
                {running ? 'Running…' : 'Run query'}
              </Button>
              {selectedList && (
                <Button color="error" onClick={handleDelete} disabled={saving}>
                  Delete list
                </Button>
              )}
            </Stack>

            {agents && agents.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  {`Query result — ${agents.length} agent${agents.length === 1 ? '' : 's'}`}
                </Typography>
                <TableContainer
                  sx={{
                    maxHeight: 280,
                    overflow: 'auto',
                    border: '1px solid rgb(var(--color-text-rgb) / 0.1)',
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={paHeadCellSx} padding="checkbox">
                          <Checkbox
                            checked={selectedCeas.size === agents.length}
                            indeterminate={selectedCeas.size > 0 && selectedCeas.size < agents.length}
                            onChange={toggleAll}
                            inputProps={{ 'aria-label': 'Select all agents' }}
                          />
                        </TableCell>
                        <TableCell sx={paHeadCellSx}>Agent</TableCell>
                        <TableCell sx={paHeadCellSx}>CEA</TableCell>
                        <TableCell sx={paHeadCellSx}>Mobile</TableCell>
                        <TableCell sx={paHeadCellSx}>Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {agents.map((agent) => (
                        <TableRow key={agent.cea} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedCeas.has(agent.cea)}
                              onChange={() => toggleAgent(agent.cea)}
                              inputProps={{ 'aria-label': `Select ${agent.cea}` }}
                            />
                          </TableCell>
                          <TableCell>{agent.name || '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{agent.cea}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{agent.mobile || '—'}</TableCell>
                          <TableCell>{agent.remarks || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} alignItems="center">
                  <Tooltip
                    title={selectedList ? '' : 'Save the list first — agents are recorded against it'}
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={handleAdd}
                        disabled={!selectedList || selectedCount === 0 || adding}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {adding
                          ? 'Adding…'
                          : `Add ${selectedCount} to ${selectedList?.projectName || 'project'}`}
                      </Button>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" sx={{ color: 'rgb(var(--color-text-rgb) / 0.55)' }}>
                    They land unassigned — the project&apos;s lead shares them out.
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default ListsTab;
